/**
 * The reply draft of a case: a text for the rep to copy into Outlook.
 *
 * `POST /cases/{id}/answer-draft` asks the drafting agent for a draft (or returns the
 * cached one), `GET` reads the cached one. Nothing is sent and nothing is written to a
 * mailbox: the server never has a send route, and the page only offers "Copy text".
 * Edits the rep makes in the page stay in the page.
 *
 * Kept free of React so the request shapes and the error wording can be tested.
 */

import { type ApiError, client } from "./client";
import type { AnswerDraft, AnswerDraftRequest, AnswerDraftState } from "./live-types";
import { detailText } from "./work-actions";

/** The fixed sentence under every draft (the quotation is in the text, nothing to attach). */
export const NOTHING_SENT_NOTE =
  "Nothing is sent. Copy the text into Outlook; the quotation is in the text.";

/** A failed draft request; `retryAfterSeconds` is read from the 429 `Retry-After` header. */
export interface AnswerDraftError extends ApiError {
  retryAfterSeconds?: number | null;
}

/** Seconds from a `Retry-After` header (seconds or an HTTP date); null when unusable. */
export function retryAfterSeconds(
  header: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const raw = (header ?? "").trim();
  if (raw === "") {
    return null;
  }
  if (/^\d+$/.test(raw)) {
    return Number.parseInt(raw, 10);
  }
  const at = Date.parse(raw);
  if (Number.isNaN(at)) {
    return null;
  }
  return Math.max(0, Math.round((at - now.getTime()) / 1000));
}

/** The cached draft of a case, or null when there is none (or the route does not exist yet). */
export async function getAnswerDraft(caseId: string): Promise<AnswerDraft | null> {
  try {
    const { data } = await client.get<{ 200: AnswerDraftState }, unknown, true>({
      url: "/cases/{case_id}/answer-draft",
      path: { case_id: caseId },
      throwOnError: true,
    });
    const draft = data?.draft;
    return draft && typeof draft === "object" ? draft : null;
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Ask for a draft. `refresh` makes a new one even when a cached one exists.
 * Throws an {@link AnswerDraftError}; on 429 it carries the wait from `Retry-After`.
 */
export async function createAnswerDraft(caseId: string, refresh = false): Promise<AnswerDraft> {
  const body: AnswerDraftRequest = { refresh };
  const result = await client.post<{ 200: AnswerDraft }, unknown, false>({
    url: "/cases/{case_id}/answer-draft",
    path: { case_id: caseId },
    body,
    headers: { "Content-Type": "application/json" },
    throwOnError: false,
  });
  if (result.error !== undefined || !result.response?.ok) {
    const failure = (result.error ?? {
      status: result.response?.status ?? 0,
      body: null,
    }) as AnswerDraftError;
    const thrown: AnswerDraftError = {
      status: failure.status ?? result.response?.status ?? 0,
      body: failure.body,
    };
    if (thrown.status === 429) {
      thrown.retryAfterSeconds = retryAfterSeconds(result.response?.headers.get("Retry-After"));
    }
    throw thrown;
  }
  return result.data as AnswerDraft;
}

/** "a minute", "5 minutes", "an hour". */
function waitText(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes >= 60) {
    const hours = Math.ceil(minutes / 60);
    return hours === 1 ? "an hour" : `${hours} hours`;
  }
  return minutes === 1 ? "a minute" : `${minutes} minutes`;
}

/** What a failed draft request reads as. Raw server text of a 5xx never reaches the user. */
export function answerDraftFailureText(error: unknown): string {
  const { status, body, retryAfterSeconds: wait } = (error ?? {}) as Partial<AnswerDraftError>;
  if (status === 429) {
    return typeof wait === "number"
      ? `You asked for many drafts in the last hour. Try again in ${waitText(wait)}.`
      : "You asked for many drafts in the last hour. Try again later.";
  }
  if (status === 422) {
    return detailText(body) ?? "A reply draft is made only for an inquiry that has a draft.";
  }
  if (status === 503) {
    return "The drafting service is not available right now. Try again later.";
  }
  if (status === 409) {
    return "The case changed while this reply was being prepared. Reload the case and prepare it again.";
  }
  if (status === 403) {
    return "Your role does not allow this.";
  }
  if (status === 404) {
    return "This case is not available.";
  }
  return "The draft could not be made. Try again in a moment.";
}

/**
 * Why the saved reply could not be read (review F10). Never "not prepared": a reply may
 * exist. No connection and an unavailable service are told apart; raw server text never
 * reaches the user.
 */
export function answerDraftReadFailureText(error: unknown): string {
  const { status } = (error ?? {}) as Partial<ApiError>;
  if (!status) {
    return "I2Q could not be reached. Check your connection and try again. A saved reply may exist, so do not prepare a new one yet.";
  }
  if (status === 503 || status === 502 || status === 504) {
    return "Saved replies cannot be read right now because the service is not available. A saved reply may exist: try again in a moment before you prepare a new one.";
  }
  if (status === 401) {
    return "Your sign-in has ended. Sign in again to see the saved reply.";
  }
  if (status === 403) {
    return "Your role does not allow reading the saved reply.";
  }
  return "The saved reply could not be read. Try again in a moment.";
}

/** What changed since a reply was made, in words (`stale_parts` of the server). */
const STALE_PART_WORDS: Record<string, string> = {
  mail: "the mail",
  customer: "the customer",
  lines: "the lines (items, quantities or decisions)",
  prices: "the prices",
  case: "the case",
};

/** True when the server says the reply no longer fits the case (review F06). */
export function isDraftStale(draft: Pick<AnswerDraft, "freshness"> | null | undefined): boolean {
  return draft?.freshness === "stale";
}

/** "Made before changes to the customer and the prices. Prepare it again before you use it." */
export function draftStaleText(draft: Pick<AnswerDraft, "stale_parts">): string {
  const words = (draft.stale_parts ?? [])
    .map((part) => STALE_PART_WORDS[part])
    .filter((word): word is string => Boolean(word));
  if (words.length === 0) {
    return "The case changed after this reply was made. Prepare it again before you use it.";
  }
  const list =
    words.length === 1
      ? (words[0] as string)
      : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
  return `This reply was made before changes to ${list}. Prepare it again before you use it.`;
}

/** Why a draft came back empty, as a sentence for the rep; null for a usable draft. */
export function emptyDraftText(
  draft: Pick<AnswerDraft, "kind" | "reason" | "body">,
): string | null {
  if (draft.reason === "budget") {
    return "The draft took too long to prepare and was stopped. Try again.";
  }
  if (draft.reason === "model_unavailable") {
    return "The drafting service is not available right now. Try again later.";
  }
  if (draft.kind === "none" || !draft.body || draft.body.trim() === "") {
    return "No reply draft fits this mail. Write the reply yourself.";
  }
  return null;
}

/** One piece of the body: plain text, or a placeholder the rep has to fill. */
export interface DraftSegment {
  text: string;
  placeholder: boolean;
}

/** Split the body so the placeholders can be highlighted; the text is never changed. */
export function splitPlaceholders(
  body: string,
  placeholders: readonly string[] | null | undefined,
): DraftSegment[] {
  const tokens = [...new Set((placeholders ?? []).filter((token) => token && token.trim() !== ""))];
  if (body === "") {
    return [];
  }
  if (tokens.length === 0) {
    return [{ text: body, placeholder: false }];
  }
  tokens.sort((a, b) => b.length - a.length);
  const escaped = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "g");
  return body
    .split(pattern)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, placeholder: tokens.includes(part) }));
}

/**
 * "12 pcs x EUR 123.45 = EUR 1,481.40" of one quoted row (the figures as the server sent
 * them). A row with the default quantity 1 (14 Oct 2026) says so, as the reply text does:
 * "1 pcs (please confirm the quantity) x EUR 73.00 = EUR 73.00".
 */
export function quotationRowText(row: {
  qty?: string | null;
  qty_default?: boolean;
  unit?: string | null;
  unit_price: string;
  currency: string;
  price_per?: number | null;
  amount?: string | null;
}): string {
  const price = `${row.currency} ${row.unit_price}${row.price_per ? ` per ${row.price_per}` : ""}`;
  if (!row.qty) {
    return `quantity to confirm, ${price} each`;
  }
  const given = row.unit ? `${row.qty} ${row.unit}` : row.qty;
  const qty = row.qty_default ? `${given} (please confirm the quantity)` : given;
  return row.amount ? `${qty} x ${price} = ${row.currency} ${row.amount}` : `${qty} x ${price}`;
}

/** Upper-case language code for the chip ("da" reads "DA"); null when unknown. */
export function languageLabel(language: string | null | undefined): string | null {
  const code = (language ?? "").trim();
  return code === "" ? null : code.toUpperCase();
}

const KIND_LABELS: Record<string, string> = {
  quote_cover: "Reply with the quotation",
  order_ack: "Order acknowledgement",
  clarification: "Question to the customer",
  holding: "Holding reply",
};

/** What kind of reply the draft is; null for `none` or an unknown kind. */
export function draftKindLabel(kind: string | null | undefined): string | null {
  return kind && Object.hasOwn(KIND_LABELS, kind) ? (KIND_LABELS[kind] as string) : null;
}
